import type { LabeledGraphicFontSchemaType } from '@vedalib/editor/lib/brand'
import type { LabeledGraphicElementValue, LabeledGraphicPoint } from '@vedalib/editor/lib/elements'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import type { RichTextValue } from '@vedalib/rich-text'
import { rtValueToText } from '@vedalib/rich-text'
import cn from 'classnames'
import * as R from 'ramda'

import RichText from 'components/form/RichText/RichText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import type { ElementFontCss } from 'services/Branding/types'
import { PreviewMode } from 'services/Store/Project/enums'
import { useUrlByUsage } from 'utils/files'

import { useLabeledGraphicContext } from '../../LabeledGraphicContext'
import * as s from './PointTooltip.module.scss'

interface IPointContentProps {
  point: LabeledGraphicPoint
  id: string
  value?: LabeledGraphicElementValue
  index: number
  font: ElementFontCss<LabeledGraphicFontSchemaType>
}

const PointContent = ({ point, id, value, index, font }: IPointContentProps) => {
  const styles = useLabeledGraphicContext((c) => c.styles)
  const mode = useLabeledGraphicContext((c) => c.mode)
  const onChange = useLabeledGraphicContext((c) => c.onChange)
  const waiting = useLabeledGraphicContext((c) => c.waiting)
  const path = useUrlByUsage(point.image, FileUsageTypeEnum.image)

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId: id,
    mode,
  })

  const isEditorMode = mode.previewMode === PreviewMode.editor

  const onChangeLabel = (labelValue: RichTextValue, index: number, field: string) => {
    if (value) {
      onChange?.(R.assocPath(['items', index, field], labelValue, value))
    }
  }

  const descriptionStr = rtValueToText(point.description)
  const descriptionName = `labeledGraphic.${index}.description`

  return (
    <div className={s.inner}>
      <div className={cn(s.content, s[mode.deviceMode])}>
        {path && (
          <div className={s.imageWrapper} style={{ borderRadius: styles.image.borderRadius }}>
            <img
              alt={point.image?.accessibility || 'Unlabeled image'}
              aria-label={point.image?.accessibility || 'Unlabeled image'}
              className={cn(s.image, s[mode.deviceMode])}
              src={path}
              style={{ objectFit: point.image?.objectFit }}
            />
            <div className={s.overlay} style={{ backgroundColor: point.overlay || undefined }} />
          </div>
        )}
        {(descriptionStr || isEditorMode) && (
          <span onMouseDown={() => onLabelSelect(descriptionName)}>
            <RichText
              active={isActiveElement && activeIndex === descriptionName}
              disabled={!isFill}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={descriptionName}
              onChange={(val) => onChangeLabel(val, index, 'description')}
              styles={font}
              value={point.description}
              waiting={waiting}
            />
          </span>
        )}
      </div>
    </div>
  )
}

export default PointContent
